import { IS_DEV } from "@/features/config";
import { createContext, useContext } from "react";
import { AnalyticsEventArgs, AnalyticsEventName } from "../types/events";
import { trackBetterStackEvent } from "../utils/betterstack";
import { hasGA } from "../utils/ga";

const trackGAPageView = (path: string) => {
  if (!hasGA()) return;
  window.gtag("event", "page_view", { page_path: path });
}

const trackGAEvent = (name: string, params?: Record<string, unknown>) => {
  if (!hasGA()) return;
  window.gtag("event", name, params);
}

interface AnalyticsContextType {
  trackPageView: (path: string) => void;
  trackEvent: <N extends AnalyticsEventName>(...args: AnalyticsEventArgs<N>) => void;
}

const AnalyticsContextValue: AnalyticsContextType = {
  trackPageView: (path) => {
    if (IS_DEV) console.log("🛠️ [Analytics] Page View:", path);
    // Better Stack auto-captures page-load / page-change — no dual-write.
    trackGAPageView(path);
  },
  trackEvent: (...args) => {
    const [name, params] = args;
    if (IS_DEV) console.log("🛠️ [Analytics] Event:", name, params);
    const payload = params as Record<string, unknown> | undefined;
    trackGAEvent(name, payload);
    trackBetterStackEvent(name, payload);
  },
};

const AnalyticsContext = createContext<AnalyticsContextType | null>(null);

export const AnalyticsProvider = ({ children }: { children: React.ReactNode }) => {
  return <AnalyticsContext.Provider value={AnalyticsContextValue}>{children}</AnalyticsContext.Provider>;
}

export const useAnalytics = () => {
  const context = useContext(AnalyticsContext);
  if (!context) {
    throw new Error("useAnalytics must be used within AnalyticsProvider");
  }
  return context;
}
